import {
  Avatar,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SvgIcon,
  Typography,
} from '@mui/material';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SignModalIcon from 'src/icons/untitled-ui/duocolor/sign-modal-icon';
import { uploadSignFileAction } from 'src/redux/actions/monthlyCloseOutAction';
import useCurrentUser from 'src/hooks/use-current-user';
import {
  closeoutScopeLabel,
  closeoutScopeNoun,
  formatSignRange,
  alertCountLabel,
  isHardCloseout,
} from './customCloseout';
import { closeoutTestId } from './test-ids';

type Props = {
  handleClose: any;
  signData: any;
  division: string;
  /**
   * BOPS-118 (`closeout-signed_by`). Read from Split by the page, not inferred from `signData`:
   * this modal opens on a period that has not been closed yet, so the row's `signedByName` is
   * necessarily absent here and can't stand in for the toggle.
   */
  signedByEnabled?: boolean;
};

function generateDummyPdfBlob() {
  const dummyPdfContent = 'This is a dummy PDF content.';
  const dummyPdfBlob = new Blob([dummyPdfContent], { type: 'application/pdf' });
  return dummyPdfBlob;
}

/** "Pat Adams" → "PA"; a single name still yields one letter rather than an empty circle. */
const initialsOf = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const SignatureAlertDocument = (props: Props) => {
  const { handleClose, signData, division, signedByEnabled = false } = props;
  const dispatch = useDispatch();
  const uploadSignatureFile = useSelector((state: any) => state.uploadSignatureFileReducer);
  const [agree, setAgree] = useState(false);
  //  BOPS-118 — the acting user is who the closeout will be recorded against, so the modal names
  //  them before the signature rather than leaving the user to infer it from their session.
  //
  //  The name shown is the CURRENT user, not the row's — nothing has been signed yet, and on a
  //  re-close after reopen the row still carries the previous closer, who is not signing now.
  const [firstName, lastName] = useCurrentUser(['firstName', 'lastName']);
  const signerName = [firstName, lastName].filter(Boolean).join(' ').trim();

  //  The whole modal follows the toggle, not just the signer row: with it off this falls back to
  //  the modal on the release branches, so turning it off is a real rollback rather than the
  //  redesign minus one line. Weekly is excluded by scope (Olivia, 10 Aug) whatever the toggle says.
  const isSignedByEnabled = signedByEnabled && isHardCloseout(division);
  const showSigner = isSignedByEnabled && Boolean(signerName);

  const errors = Number(signData?.errors ?? 0);
  const warnings = Number(signData?.warnings ?? 0);
  const scopeLabel = closeoutScopeLabel(division);
  const scopeNoun = closeoutScopeNoun(division);
  const dateRange = formatSignRange(signData?.range?.startDate, signData?.range?.endDate);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append('consentForm', generateDummyPdfBlob());
    if (agree) {
      dispatch(
        uploadSignFileAction({
          data: formData,
          param: {
            startDate: signData?.range?.startDate,
            endDate: signData?.range?.endDate,
            division: division,
          },
        })
      );
    }
  };

  const handleChangesStatus = (e: any) => {
    setAgree(e?.target?.checked);
  };

  return (
    <>
      <DialogTitle
        alignItems={'center'}
        id="alert-dialog-title"
        mt={2}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              background: '#F5F7FF',
            }}
          >
            <SvgIcon>
              <SignModalIcon />
            </SvgIcon>
          </Avatar>
          <Typography
            variant="h6"
            color="initial"
          >
            Sign Certification
          </Typography>
        </Box>
      </DialogTitle>
      {/*  Toggle OFF — the modal exactly as it stands on the release branches (identical on
          release_5.14.0, release_5.13.0 and main). Reproduced rather than approximated, so the
          rollback lands on a UI that has already shipped, not a third variant. */}
      {!isSignedByEnabled ? (
        <DialogContent sx={{ ml: 4 }}>
          <DialogContentText id="alert-dialog-description">
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                checked={agree}
                onChange={handleChangesStatus}
                inputProps={
                  {
                    'data-testid': closeoutTestId('certify'),
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
              <Typography variant="body2">
                I certify that I have reviewed and resolved all outstanding issues associated with
                this closeout.
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
      ) : (
        <DialogContent id="alert-dialog-description">
          {/*  The period being signed, named up front — the modal opens over a list of rows, so the
            signature needs to state which one it applies to. */}
          {dateRange && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                mb: 1.5,
                borderRadius: '8px',
                backgroundColor: '#F3F4F6',
              }}
            >
              <CalendarTodayOutlined sx={{ fontSize: 16, color: '#6C737F' }} />
              <Typography
                variant="body2"
                sx={{ color: '#111927', fontWeight: 500 }}
              >
                {`${dateRange} · ${scopeLabel}`}
              </Typography>
            </Box>
          )}

          {/*  Close is only ever offered on a zero-error period, so the reassurance is stated rather
            than left implicit — but it is dropped if errors ever do reach this modal. */}
          <Typography
            variant="body2"
            sx={{ color: '#6C737F', mb: 2 }}
          >
            <Box
              component="span"
              sx={{ color: errors === 0 ? '#10B981' : '#B42318', fontWeight: 600 }}
            >
              {alertCountLabel(errors, 'Error')}
            </Box>
            {` · ${alertCountLabel(warnings, 'Warning')}`}
            {errors === 0 ? ` — this ${scopeNoun} is ready to close.` : ''}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1.5,
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              backgroundColor: '#F9FAFB',
            }}
          >
            <Checkbox
              checked={agree}
              onChange={handleChangesStatus}
              sx={{ p: 0.5 }}
              inputProps={
                {
                  'aria-label':
                    'I certify that I have reviewed and resolved all outstanding issues associated with this closeout.',
                  'data-testid': closeoutTestId('certify'),
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
            />
            <Typography
              variant="body2"
              sx={{ color: '#111927' }}
            >
              I certify that I have reviewed and resolved all outstanding issues associated with
              this closeout.
            </Typography>
          </Box>

          {/*  Hidden when the BOPS-118 toggle is off, and until the profile has loaded. */}
          {showSigner && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: 12,
                  backgroundColor: '#EBEEFE',
                  color: '#6366F1',
                }}
              >
                {initialsOf(signerName)}
              </Avatar>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
              >
                Signing as{' '}
                <Box
                  component="span"
                  sx={{ color: '#111927', fontWeight: 600 }}
                >
                  {signerName}
                </Box>
              </Typography>
            </Box>
          )}
        </DialogContent>
      )}
      {/*  Shared: only the submit label differs. The submit path itself is identical either way —
          the backend records the signer regardless of what the modal shows. */}
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          variant="text"
          data-testid={closeoutTestId('sign-cancel')}
        >
          <Typography
            variant="buttonText"
            color={'#384250'}
          >
            Cancel
          </Typography>
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!agree || uploadSignatureFile?.loading}
          data-testid={closeoutTestId('sign-submit')}
        >
          {isSignedByEnabled ? <>Close &amp; sign</> : 'Submit'}
        </Button>
      </DialogActions>
    </>
  );
};

export default SignatureAlertDocument;
